import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { format, addDays } from 'date-fns';
import { es } from 'date-fns/locale';

const hoy = format(new Date(), 'yyyy-MM-dd');

const DIAS_ENVIO = { 'lun,mie,vie,sab': [1,3,5,6], 'lun,vie': [1,5] };
function proximoDiaEnvio(diasStr) {
  const dias = DIAS_ENVIO[diasStr] || [1,3,5,6];
  const ahora = new Date();
  for (let i = 1; i <= 7; i++) {
    const d = addDays(ahora, i);
    if (dias.includes(d.getDay())) return format(d, 'yyyy-MM-dd');
  }
  return format(addDays(ahora, 1), 'yyyy-MM-dd');
}

const ESTADO_COLORS = {
  borrador: { bg: '#FEF3C7', color: '#92400E', label: 'Borrador' },
  enviado:  { bg: '#D1FAE5', color: '#065F46', label: 'Enviado ✓' },
  recibido: { bg: '#DBEAFE', color: '#1E40AF', label: 'Recibido ✓' },
};

function Pill({ activo, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700,
      border: `2px solid ${activo ? '#1a1a2e' : '#E5E7EB'}`,
      background: activo ? '#1a1a2e' : 'white', color: activo ? 'white' : '#374151',
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>{children}</button>
  );
}

// ─────────────────────────────────────────
// PANTALLA — /envios
// ─────────────────────────────────────────
export default function Envios() {
  const { usuario } = useAuth();

  const [tab, setTab] = useState('nuevo'); // nuevo | historial
  const [bodegas, setBodegas] = useState([]);
  const [sabores, setSabores] = useState([]);
  const [congeladores, setCongeladores] = useState([]);
  const [bodegaId, setBodegaId] = useState('');
  const [bodegaNombre, setBodegaNombre] = useState('');
  const [fecha, setFecha] = useState(hoy);
  const [detalle, setDetalle] = useState({});
  const [observacion, setObs] = useState('');
  const [sugerencia, setSugerencia] = useState(null);
  const [cargandoSug, setCargandoSug] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [envioDetalle, setEnvioDetalle] = useState(null);

  // Bodegas destino (PDV) + sabores + config congeladores
  useEffect(() => {
    API.get('/bodegas').then(r => {
      const pvs = r.data.filter(b => b.tipo === 'punto_venta');
      setBodegas(pvs);
      if (pvs.length) { setBodegaId(String(pvs[0].id)); setBodegaNombre(pvs[0].nombre); }
    }).catch(() => toast.error('No se pudieron cargar los puntos de venta'));
    API.get('/helados/sabores').then(r => setSabores(r.data)).catch(() => {});
    API.get('/congeladores').then(r => setCongeladores(r.data)).catch(() => {});
  }, []);

  const cambiarBodega = (b) => { setBodegaId(String(b.id)); setBodegaNombre(b.nombre); setDetalle({}); };

  const conf = congeladores.find(c => String(c.bodega_id) === String(bodegaId));
  const capacidad = conf?.capacidad_bandejas || 0;
  const maxEnvio = conf?.max_envio || capacidad;
  const limiteConfigurado = maxEnvio > 0;
  const totalDetalle = Object.values(detalle).reduce((t, v) => t + (parseInt(v) || 0), 0);
  const excedeMax = limiteConfigurado && totalDetalle > maxEnvio;
  const fechaEnvioSugerida = conf ? proximoDiaEnvio(conf.dias_envio) : null;

  // Sugerencia
  const cargarSugerencia = useCallback(() => {
    if (!bodegaId) return;
    setCargandoSug(true);
    API.get(`/envios/sugerencia/${bodegaId}`, { params: { fecha_envio: fecha } })
      .then(r => setSugerencia(r.data))
      .catch(() => setSugerencia(null))
      .finally(() => setCargandoSug(false));
  }, [bodegaId, fecha]);
  useEffect(() => { if (tab === 'nuevo') cargarSugerencia(); }, [tab, cargarSugerencia]);

  // Historial
  useEffect(() => {
    if (tab !== 'historial') return;
    API.get('/envios', { params: { bodega_id: bodegaId || null } }).then(r => setHistorial(r.data)).catch(() => {});
  }, [tab, bodegaId]);

  const verDetalle = async (id) => {
    try { const r = await API.get(`/envios/${id}`); setEnvioDetalle(r.data); }
    catch { toast.error('Error cargando detalle'); }
  };

  const aplicarSugerencia = () => {
    if (!sugerencia?.sugerencia?.length) return;
    const nuevo = {};
    sugerencia.sugerencia.forEach(s => { nuevo[s.sabor_id] = s.bandejas; });
    setDetalle(nuevo);
    toast.success('Sugerencia aplicada — ajusta si hace falta');
  };

  const handleGuardar = async (enviar = false) => {
    if (!bodegaId) { toast.error('Selecciona un PDV'); return; }
    if (totalDetalle === 0) { toast.error('Agrega al menos un sabor'); return; }
    if (excedeMax) toast(`⚠️ Superas el máximo sugerido (${maxEnvio} bandejas) — se registrará igual`, { icon: '⚠️' });
    setGuardando(true);
    try {
      const det = Object.entries(detalle).filter(([,v]) => parseInt(v) > 0).map(([sabor_id, bandejas]) => ({ sabor_id: parseInt(sabor_id), bandejas: parseInt(bandejas) }));
      await API.post('/envios', { bodega_id: bodegaId, fecha, detalle: det, observacion, enviar });
      toast.success(enviar ? '✅ Envío registrado y confirmado' : '💾 Borrador guardado');
      setDetalle({}); setObs('');
      cargarSugerencia();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setGuardando(false); }
  };

  const setCant = (saborId, val) => setDetalle(d => ({ ...d, [saborId]: Math.max(0, val) }));

  const mostrarBarra = tab === 'nuevo';

  return (
    <div style={{ paddingBottom: mostrarBarra ? 78 : 0 }}>
      <div style={{ marginBottom: 14 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#111827' }}>🚚 Envíos de Helado</h1>
        <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>Producción → {bodegaNombre || 'PDV'}</div>
      </div>

      {/* Destino PDV */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, overflowX: 'auto', paddingBottom: 4 }}>
        {bodegas.map(b => (
          <Pill key={b.id} activo={String(bodegaId) === String(b.id)} onClick={() => cambiarBodega(b)}>{b.nombre}</Pill>
        ))}
      </div>

      {/* Fecha */}
      <div style={{ marginBottom: 12 }}>
        <input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
          style={{ padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none' }} />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Pill activo={tab === 'nuevo'} onClick={() => { setTab('nuevo'); setEnvioDetalle(null); }}>📦 Nuevo envío</Pill>
        <Pill activo={tab === 'historial'} onClick={() => { setTab('historial'); setEnvioDetalle(null); }}>📋 Historial</Pill>
      </div>

      {tab === 'historial' ? (
        envioDetalle ? (
          <div style={{ background: 'white', borderRadius: 14, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14, color: '#111827' }}>🚚 Envío #{envioDetalle.id} — {envioDetalle.bodega_nombre}</div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                  {envioDetalle.fecha ? format(new Date(envioDetalle.fecha.slice(0,10)+'T12:00'), "d 'de' MMMM yyyy", { locale: es }) : '—'}
                </div>
              </div>
              <button onClick={() => setEnvioDetalle(null)} style={{ background: 'none', border: 'none', color: '#2563EB', fontSize: 13, cursor: 'pointer', fontWeight: 600, padding: 0 }}>← Volver</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {envioDetalle.detalle?.map(d => (
                <div key={d.sabor_id} style={{ background: '#F9FAFB', borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                  <div style={{ fontWeight: 800, fontSize: 20, color: '#1a1a2e' }}>{d.bandejas}</div>
                  <div style={{ fontSize: 11, color: '#6B7280' }}>bandejas</div>
                  <div style={{ fontWeight: 700, fontSize: 12, color: '#111827', marginTop: 4 }}>{d.sabor_nombre}</div>
                </div>
              ))}
            </div>
          </div>
        ) : historial.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9CA3AF' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
            <div style={{ fontWeight: 600 }}>Sin envíos aún</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {historial.map(h => {
              const ec = ESTADO_COLORS[h.estado] || ESTADO_COLORS.borrador;
              return (
                <button key={h.id} onClick={() => verDetalle(h.id)} style={{ background: 'white', borderRadius: 14, padding: '12px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', cursor: 'pointer', border: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', textAlign: 'left' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{h.bodega_nombre}</div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                      {(() => { try { return format(new Date(h.fecha.slice(0,10)+'T12:00'), "d 'de' MMM", { locale: es }); } catch { return h.fecha; } })()}
                    </div>
                  </div>
                  <span style={{ padding: '4px 10px', borderRadius: 20, fontWeight: 700, fontSize: 11, background: ec.bg, color: ec.color }}>{ec.label}</span>
                </button>
              );
            })}
          </div>
        )
      ) : (
        <>
          {/* Sugerencia */}
          {cargandoSug ? (
            <div style={{ background: 'white', borderRadius: 14, padding: 20, textAlign: 'center', color: '#9CA3AF', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: 14 }}>Calculando sugerencia...</div>
          ) : sugerencia && !sugerencia.sin_inventario ? (
            <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: 14 }}>
              <div style={{ background: '#1a1a2e', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ color: 'white', fontWeight: 800, fontSize: 13 }}>💡 Sugerencia</div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, marginTop: 2 }}>
                    Próximo envío: <strong style={{ color: 'white' }}>{fechaEnvioSugerida ? format(new Date(fechaEnvioSugerida+'T12:00'), "EEE d MMM", { locale: es }) : '—'}</strong>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: 'white', fontWeight: 800, fontSize: 20 }}>{sugerencia.bandejas_a_enviar}</div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>bandejas</div>
                </div>
              </div>
              <div style={{ padding: '10px 16px', background: '#F8FAFC', display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 11 }}>
                <span style={{ color: '#374151' }}>🧊 Ocupadas: <strong>{sugerencia.bandejas_ocupadas}</strong></span>
                <span style={{ color: '#374151' }}>📦 Libre: <strong>{sugerencia.espacio_libre}</strong></span>
              </div>
              {sugerencia.sugerencia?.length > 0 && (
                <>
                  <div style={{ padding: '10px 16px', display: 'flex', gap: 6, flexWrap: 'wrap', borderTop: '1px solid #F3F4F6' }}>
                    {sugerencia.sugerencia.map(s => (
                      <div key={s.sabor_id} style={{ background: '#EEF2FF', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, color: '#4338CA' }}>{s.nombre}: {s.bandejas}🧊</div>
                    ))}
                  </div>
                  <div style={{ padding: '10px 16px' }}>
                    <button onClick={aplicarSugerencia} style={{ width: '100%', padding: '9px 0', border: 'none', borderRadius: 8, background: '#4338CA', color: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>✨ Aplicar sugerencia</button>
                  </div>
                </>
              )}
            </div>
          ) : sugerencia?.sin_inventario ? (
            <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 12, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#92400E', fontWeight: 600 }}>
              ⚠️ Este PDV aún no tiene inventario bloqueado. No se puede calcular sugerencia.
            </div>
          ) : null}

          {/* Barra bandejas */}
          <div style={{ background: 'white', borderRadius: 14, padding: 14, marginBottom: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
              <span style={{ fontWeight: 700 }}>Bandejas a enviar: <span style={{ color: excedeMax ? '#EF4444' : '#10B981', fontSize: 16 }}>{totalDetalle}</span></span>
              <span style={{ color: '#6B7280', fontSize: 12 }}>{limiteConfigurado ? `Máx. sugerido: ${maxEnvio}` : 'Sin límite configurado'}</span>
            </div>
            {limiteConfigurado && (
              <div style={{ height: 8, background: '#E5E7EB', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(totalDetalle/maxEnvio*100,100)}%`, background: excedeMax ? '#EF4444' : totalDetalle/maxEnvio > 0.85 ? '#F59E0B' : '#10B981', borderRadius: 4, transition: 'width 0.2s' }} />
              </div>
            )}
            {excedeMax && (
              <div style={{ marginTop: 6, fontSize: 11, color: '#B45309', background: '#FFFBEB', padding: '5px 8px', borderRadius: 6, fontWeight: 600 }}>
                ⚠️ Superas el máximo sugerido — es solo referencia, puedes continuar.
              </div>
            )}
          </div>

          {/* Grid sabores */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {sabores.map(s => {
              const cant = detalle[s.id] || 0;
              return (
                <div key={s.id} style={{ background: 'white', border: '2px solid', borderColor: cant > 0 ? '#6366F1' : '#E5E7EB', borderRadius: 12, padding: '10px 12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', marginBottom: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.nombre}{s.es_premium ? ' ⭐' : ''}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <button onClick={() => setCant(s.id, cant - 1)} disabled={cant <= 0} style={{ width: 30, height: 30, border: '2px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: cant <= 0 ? 'default' : 'pointer', fontSize: 16, fontWeight: 700, color: '#374151', opacity: cant <= 0 ? 0.4 : 1 }}>−</button>
                    <div style={{ width: 36, textAlign: 'center', fontWeight: 800, fontSize: 15 }}>{cant}</div>
                    <button onClick={() => setCant(s.id, cant + 1)} style={{ width: 30, height: 30, border: '2px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 16, fontWeight: 700, color: '#374151' }}>+</button>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ background: 'white', borderRadius: 14, padding: 14, marginTop: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 8 }}>📝 Observación</label>
            <textarea value={observacion} onChange={e => setObs(e.target.value)} placeholder="Opcional..." rows={2}
              style={{ width: '100%', padding: '10px 12px', border: '2px solid #E5E7EB', borderRadius: 10, fontSize: 14, resize: 'vertical', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
          </div>
        </>
      )}

      {mostrarBarra && (
        <div style={{ position: 'fixed', left: 0, right: 0, bottom: 'calc(70px + env(safe-area-inset-bottom, 0px))', display: 'flex', gap: 10, padding: '10px 20px', background: 'white', borderTop: '1px solid #E5E7EB', boxShadow: '0 -2px 10px rgba(0,0,0,0.06)', zIndex: 90 }}>
          <button onClick={() => handleGuardar(false)} disabled={guardando}
            style={{ flex: 1, padding: '13px 0', border: '2px solid #E5E7EB', borderRadius: 12, background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#374151' }}>
            {guardando ? '⏳' : '💾 Borrador'}
          </button>
          <button onClick={() => handleGuardar(true)} disabled={guardando || totalDetalle === 0}
            style={{ flex: 2, padding: '13px 0', border: 'none', borderRadius: 12, background: totalDetalle === 0 ? '#E5E7EB' : excedeMax ? '#F59E0B' : '#1a1a2e', color: totalDetalle === 0 ? '#9CA3AF' : 'white', cursor: totalDetalle === 0 ? 'default' : 'pointer', fontSize: 14, fontWeight: 700 }}>
            {guardando ? '⏳ Guardando...' : excedeMax ? '⚠️ Confirmar de todos modos' : '✅ Confirmar envío'}
          </button>
        </div>
      )}
    </div>
  );
}
